import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import axios from "../../utils/axios/v1/userAxios";
import useAuth from "../../hooks/useAuth";

/**
 * Licensing v2 — effective-features context.
 *
 * Backed by `GET /api/user/v1/licensing/effective-features` (tenant-scoped via
 * the regular JWT/session). Response shape:
 *
 *   { org_id: "<uuid>", features: { "waf.signature.owasp": true, ... } }
 *
 * Values are `true | false | number | null`.
 *
 * Hooks:
 *   - `useEffectiveFeature(key)`   → returns current value (undefined while loading)
 *   - `useEffectiveFeatures()`     → returns the full read-only map
 *
 * Static helper:
 *   - `isFeatureEnabledV2(map, key)` → bool|number, the same shape the legacy
 *     `isFeatureEnabled(FeatureId.X)` returned. Used by the JWTAuth0Context
 *     shim so existing call sites keep working.
 *
 * Failure modes are deliberate: if the endpoint 404s (CP not yet upgraded) we
 * fall back to "all features off" — same behaviour callers see today — rather
 * than crashing the app. That preserves the pre-fix baseline until both PRs
 * land together.
 */

// Module-scope mirror — the legacy `isFeatureEnabled` callback in
// JWTAuth0Context needs to read the v2 map without subscribing to React
// context (it's invoked from a useCallback that does not live under this
// provider's tree-position concerns). The provider keeps this in sync.
let _featuresRef = {};
let _loaded = false;

export const getEffectiveFeaturesSnapshot = () => _featuresRef;
export const hasLoadedEffectiveFeatures = () => _loaded;

/**
 * Read a key from a features map and normalise the return shape to what the
 * legacy `isFeatureEnabled(FeatureId.X)` produced: booleans for boolean
 * features, numbers for numeric features, `false` if the key is missing.
 */
export const isFeatureEnabledV2 = (featuresMap, key) => {
  if (!featuresMap || !key) return false;
  const v = featuresMap[key];
  if (v === undefined || v === null) return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v;
  return Boolean(v);
};

const EffectiveFeaturesContext = createContext({
  features: {},
  loading: true,
  error: null,
  loaded: false,
  refresh: () => {},
});

function EffectiveFeaturesProvider({ children }) {
  const { user, sauser, isAuthenticated } = useAuth();
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const orgIdRef = useRef();

  const orgId = user?.organisation?.uid || user?.organisation?.id || sauser?.organisation?.uid || sauser?.organisation?.id || null;

  const fetchFeatures = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get("/licensing/effective-features");
      const map = response?.data?.features || {};
      setFeatures(map);
      _featuresRef = map;
      _loaded = true;
      setLoaded(true);
    } catch (err) {
      // 404 → CP endpoint not deployed yet. Match the pre-fix behaviour
      // (all gates closed) rather than crashing the app.
      const status = err?.statusCode || err?.status || err?.response?.status;
      if (status === 404) {
        // eslint-disable-next-line no-console
        console.warn("[licensing-v2] /licensing/effective-features not available; falling back to all-off");
      } else {
        // eslint-disable-next-line no-console
        console.warn("[licensing-v2] failed to fetch effective features:", err?.message || err);
      }
      setFeatures({});
      _featuresRef = {};
      _loaded = true; // we tried — don't keep callers in "loading" forever
      setLoaded(true);
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch when we have an authenticated user/sauser; refetch when org changes.
    if (!isAuthenticated) {
      return;
    }
    if (!user && !sauser) {
      return;
    }
    if (orgIdRef.current === orgId) {
      return;
    }
    orgIdRef.current = orgId;
    fetchFeatures();
  }, [isAuthenticated, user, sauser, orgId, fetchFeatures]);

  const value = useMemo(
    () => ({
      features,
      loading,
      error,
      loaded,
      refresh: fetchFeatures,
    }),
    [features, loading, error, loaded, fetchFeatures]
  );

  return <EffectiveFeaturesContext.Provider value={value}>{children}</EffectiveFeaturesContext.Provider>;
}

const useEffectiveFeatures = () => {
  const ctx = useContext(EffectiveFeaturesContext);
  return ctx.features;
};

const useEffectiveFeature = (key) => {
  const ctx = useContext(EffectiveFeaturesContext);
  if (!ctx.loaded) return undefined;
  return ctx.features?.[key];
};

const useEffectiveFeaturesState = () => useContext(EffectiveFeaturesContext);

export {
  EffectiveFeaturesContext,
  EffectiveFeaturesProvider,
  useEffectiveFeature,
  useEffectiveFeatures,
  useEffectiveFeaturesState,
};
